import { useMemo } from 'react';

import type { IUseTransferViewTrackingParams } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import { useTrackOnce } from '@app/dashboard/hooks/useTrackOnce';
import { TRANSFERS_STATUS } from '@app/dashboard/constants/balance_and_transfers';

import {
  TransferViewEventTracking,
  TransferViewFailedEventTracking,
} from '../utils/events-tracking';

export const useSingleTransferViewTracking = ({
  id,
  isFetching,
  isError,
  transfer_summary,
  header,
  transfer_progress,
  entryPoint,
}: IUseTransferViewTrackingParams) => {
  const FAILED_STATUS_KEYS: string[] = useMemo(
    () => [
      TRANSFERS_STATUS.FAILED,
      TRANSFERS_STATUS.REJECT_INSTANT,
      TRANSFERS_STATUS.RETURNED_BY_BANK,
    ],
    []
  );

  useTrackOnce({
    trackFn: () => {
      const statusKey = header?.status?.key;

      const sharedProperties = {
        transferId: transfer_summary?.transfer_id || '',
        status: statusKey,
        currency: header?.currency,
        entryPoint,
      };

      const isFailedStatus =
        !!statusKey && FAILED_STATUS_KEYS.includes(statusKey);

      TransferViewEventTracking({
        ...sharedProperties,
        netAmount: header?.amount,
        settlementType: transfer_summary?.settlement_type_key,
        timelineSteps: transfer_progress,
      });

      if (isFailedStatus) {
        const failedStep = transfer_progress?.find(
          (step) => !!step?.key && FAILED_STATUS_KEYS.includes(step.key)
        );

        const bankDestination = failedStep?.detail?.bank_destination || '';

        TransferViewFailedEventTracking({
          ...sharedProperties,
          timelineType: transfer_summary?.settlement_type_key ?? '',
          bankDestination,
        });
      }
    },
    deps: [id, isFetching, isError],
    shouldTrack: !!id && !isFetching && !isError,
  });
};
